import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight } from 'react-icons/fi';
import { FaBuilding, FaCompass, FaGears, FaLandmark, FaMapLocationDot, FaMugHot, FaSchool, FaStethoscope } from 'react-icons/fa6';
import { useTranslation } from 'react-i18next';

type IndustryItem = {
  id: number;
  icon: React.ComponentType<{ className?: string }>;
  titleKey: string;
};

const industries: IndustryItem[] = [
  { id: 1, icon: FaMapLocationDot, titleKey: 'weServeSection.industries.mining' },
  { id: 2, icon: FaStethoscope, titleKey: 'weServeSection.industries.healthcare' },
  { id: 3, icon: FaBuilding, titleKey: 'weServeSection.industries.corporate' },
  { id: 4, icon: FaLandmark, titleKey: 'weServeSection.industries.government' },
  { id: 5, icon: FaMugHot, titleKey: 'weServeSection.industries.hospitality' },
  { id: 6, icon: FaCompass, titleKey: 'weServeSection.industries.aviation' },
  { id: 7, icon: FaSchool, titleKey: 'weServeSection.industries.education' },
  { id: 8, icon: FaGears, titleKey: 'weServeSection.industries.industrial' }
];

const WeServe: React.FC = () => {
  const ArrowIcon = FiArrowRight as React.ComponentType<{ className?: string }>;
  const { t } = useTranslation();

  return (
    <section className="py-14 sm:py-20 lg:py-24 bg-[#0A1628] relative overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10 sm:mb-14">
          <p className="text-teal-400 text-xs font-bold uppercase tracking-[0.2em] mb-3">{t('weServeSection.eyebrow')}</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight leading-tight">{t('weServeSection.title')}</h2>
          <p className="text-white/40 mt-3 sm:mt-4 max-w-lg mx-auto text-sm sm:text-base px-2 sm:px-0">
            {t('weServeSection.description')}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {industries.map((item) => (
            <article
              key={item.id}
              className="group rounded-2xl border border-white/10 bg-white/[0.04] p-4 sm:p-5 transition-all duration-300 hover:bg-white/[0.07] hover:border-teal-300/40 cursor-default"
            >
              <div className="flex items-center gap-3">
                <div className="text-teal-300 transition-transform duration-300 group-hover:scale-110 group-hover:text-teal-200">
                  <item.icon className="h-5 w-5" />
                </div>
                <h3 className="text-white text-sm sm:text-base font-semibold tracking-wide">{t(item.titleKey)}</h3>
              </div>
            </article>
          ))}
        </div>

        <div className="text-center mt-8 sm:mt-12">
          <Link to="/industries" className="inline-flex items-center gap-2 text-teal-400 font-semibold text-sm hover:gap-3 transition-all">
            {t('weServeSection.cta')}
            <span aria-hidden="true" className="inline-flex items-center">
              <ArrowIcon className="h-4 w-4" />
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default WeServe;
